"use client";

import { useEffect, useRef, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import ChartColumnBigIcon from "@hugeicons/core-free-icons/ChartColumnBigIcon";
import GlobeIcon from "@hugeicons/core-free-icons/GlobeIcon";
import Message01Icon from "@hugeicons/core-free-icons/Message01Icon";
import SentIcon from "@hugeicons/core-free-icons/SentIcon";
import UserGroupIcon from "@hugeicons/core-free-icons/UserGroupIcon";
import { siteContent } from "@/lib/site-content";
import { cn } from "@/lib/utils";
import { useColorTheme } from "@/components/providers/color-theme-provider";

type ImpactFormat = "m" | "k" | "plain";

function formatImpactValue(n: number, format: ImpactFormat): string {
  const v = Math.max(0, n);
  if (format === "plain") {
    return `${Math.round(v)}+`;
  }
  if (format === "m") {
    if (v >= 1_000_000) {
      const m = v / 1_000_000;
      const mRounded = Math.round(m * 10) / 10;
      return `${mRounded}M+`;
    }
    if (v >= 1000) return `${Math.round(v / 1000)}K+`;
    return `${Math.round(v)}+`;
  }
  if (v >= 1000) return `${Math.round(v / 1000)}K+`;
  return `${Math.round(v)}+`;
}

const CARD_ICONS = {
  users: UserGroupIcon,
  message: Message01Icon,
  globe: GlobeIcon,
} as const;

const ACCENT = {
  pink: {
    iconWrap: "bg-pink-500/12 text-pink-600 dark:bg-pink-500/18 dark:text-pink-400",
    bar: "bg-pink-500",
    number: "text-pink-600 dark:text-pink-400",
  },
  orange: {
    iconWrap: "bg-orange-500/12 text-orange-600 dark:bg-orange-500/18 dark:text-orange-400",
    bar: "bg-orange-500",
    number: "text-orange-600 dark:text-orange-400",
  },
  emerald: {
    iconWrap: "bg-emerald-500/12 text-emerald-600 dark:bg-emerald-500/18 dark:text-emerald-400",
    bar: "bg-emerald-500",
    number: "text-emerald-600 dark:text-emerald-400",
  },
} as const;

export default function ImpactStats() {
  const content = siteContent.impact;
  const { themeColor } = useColorTheme();
  const sectionRef = useRef<HTMLElement>(null);
  const heroMetricRef = useRef<HTMLParagraphElement>(null);
  const cardMetricRefs = useRef<(HTMLParagraphElement | null)[]>([]);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const frameIds = new Set<number>();
    let hasStarted = false;
    const animateCounter = (
      element: HTMLParagraphElement | null,
      start: number,
      end: number,
      format: ImpactFormat,
      delay: number,
    ) => {
      if (!element) return;
      const duration = 1_500;
      const startTime = performance.now() + delay;
      const tick = (now: number) => {
        const progress = Math.min(Math.max((now - startTime) / duration, 0), 1);
        const eased = 1 - (1 - progress) ** 3;
        element.textContent = formatImpactValue(start + (end - start) * eased, format);
        if (progress < 1) {
          const frameId = requestAnimationFrame(tick);
          frameIds.add(frameId);
        }
      };
      const frameId = requestAnimationFrame(tick);
      frameIds.add(frameId);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || hasStarted) return;
        hasStarted = true;
        setHasEntered(true);
        animateCounter(heroMetricRef.current, content.heroMetric.start, content.heroMetric.end, content.heroMetric.format, 0);
        content.cards.forEach((card, index) => {
          animateCounter(cardMetricRefs.current[index], card.start, card.end, card.format, 100 + index * 90);
        });
        observer.disconnect();
      },
      { threshold: 0.22 },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      frameIds.forEach((frameId) => cancelAnimationFrame(frameId));
    };
  }, [content]);

  return (
    <section
      ref={sectionRef}
      id="impact"
      className="relative w-full overflow-x-clip bg-[#FBFBFB] dark:bg-[#101113]"
    >
      <div className="relative mx-auto flex w-full justify-center">
        <div
          className="relative w-full shrink-0 overflow-hidden bg-[#fde047]"
        >
          <img
            src="/3barCornerForTopLeft.png"
            alt=""
            aria-hidden
            className="pointer-events-none absolute -left-6 -top-6 z-[2] hidden w-10 select-none opacity-95 sm:block"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 hidden items-center justify-center pt-[clamp(3.5rem,14vh,9rem)] sm:flex"
          >
            {/* Large, soft “halo” rings: cream/white on yellow — readable dashes without harsh contrast */}
            <div className="relative aspect-square w-[min(132vw,960px)] max-w-[960px] sm:w-[min(118vw,920px)]">
              {/* Outer: CCW — wider dashes, airy stroke */}
              <svg
                className="animate-impact-ring-ccw absolute inset-0 size-full drop-shadow-[0_0_1px_rgb(255_255_255/0.35)]"
                viewBox="0 0 100 100"
                fill="none"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="47.25"
                  className="stroke-white/50 dark:stroke-amber-100/18"
                  strokeWidth="2.35"
                  strokeDasharray="7.5 5.5"
                  strokeLinecap="round"
                />
              </svg>
              {/* Inner: CW — slightly softer + smaller gap pattern */}
              <svg
                className="animate-impact-ring-cw absolute inset-[7.5%] size-[85%] drop-shadow-[0_0_1px_rgb(255_253_231/0.4)]"
                viewBox="0 0 100 100"
                fill="none"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="47.25"
                  className="stroke-white/38 dark:stroke-amber-50/14"
                  strokeWidth="2.1"
                  strokeDasharray="6.25 5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>

          <div
            aria-hidden
            className="pointer-events-none absolute right-[6%] top-[14%] hidden text-amber-700/20 dark:text-amber-400/15 sm:block"
          >
            <HugeiconsIcon
              icon={SentIcon}
              size={32}
              color="currentColor"
              className="rotate-12 text-amber-700/20 dark:text-amber-400/15"
              aria-hidden
            />
          </div>

          <div className="relative z-[1] w-full px-4 py-16 sm:container-padding sm:section-y">
            <div className={cn("mx-auto max-w-3xl text-center", hasEntered && "impact-header-enter")}>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-violet-800 dark:bg-black/40 dark:text-black sm:mb-4 sm:text-xs">
                <HugeiconsIcon
                  icon={ChartColumnBigIcon}
                  size={14}
                  color="currentColor"
                  className="text-violet-600 dark:text-black"
                  aria-hidden
                />
                {content.eyebrow}
              </div>
              <h2 className="text-balance text-3xl font-bold tracking-tight text-foreground dark:text-black sm:text-5xl lg:text-5xl lg:leading-[1.1]">
                {content.titleBefore}
                <span className={cn("gradient-text rounded-md border border-dashed px-2 py-0.5 inline-block rotate-[-2deg]", themeColor === "yellow" ? "text-black border-black" : "border-current")}>{content.titleAccent}</span>
              </h2>
              <p className="mx-auto mt-2.5 max-w-md text-[13px] text-muted-foreground dark:text-black/70 sm:mt-3 sm:max-w-xl sm:text-base">
                {content.subtitle}
              </p>
            </div>

            <div className="relative mx-auto mt-8 max-w-xl text-center sm:mt-12">
              <p
                ref={heroMetricRef}
                className={cn("text-[clamp(3rem,18vw,8.5rem)] font-black leading-none tracking-tight gradient-text tabular-nums", hasEntered && "impact-metric-enter", themeColor === "yellow" && "text-black")}
              >
                {formatImpactValue(content.heroMetric.end, content.heroMetric.format)}
              </p>
              <p className="mt-1.5 text-[13px] font-medium text-foreground dark:text-black sm:mt-2 sm:text-base">
                {content.heroMetric.label}
              </p>
            </div>

            <div
              className={cn(
                "mx-auto mt-7 grid max-w-5xl gap-2.5 sm:mt-12 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3",
                hasEntered && "impact-cards-enter",
              )}
            >
              {content.cards.map((card, i) => {
                const cardIcon = CARD_ICONS[card.icon];
                const accent = ACCENT[card.accent];
                return (
                  <article
                    key={card.label}
                    className="relative flex flex-col rounded-2xl bg-white px-3.5 pb-3.5 pt-3 dark:bg-black sm:px-4 sm:pb-4 sm:pt-3.5"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "flex size-11 shrink-0 items-center justify-center rounded-xl sm:size-12",
                          accent.iconWrap,
                        )}
                      >
                        <HugeiconsIcon icon={cardIcon} size={21} color="currentColor" aria-hidden />
                      </div>
                      <div className="min-w-0 flex-1 text-left">
                        <p
                          className={cn(
                            "text-[1.15rem] font-bold tabular-nums tracking-tight sm:text-2xl",
                            accent.number,
                          )}
                          ref={(element) => {
                            cardMetricRefs.current[i] = element;
                          }}
                        >
                          {formatImpactValue(card.end, card.format)}
                        </p>
                        <p className="mt-0.5 text-[13px] font-medium text-zinc-600 sm:text-sm">
                          {card.label}
                        </p>
                      </div>
                    </div>
                    <div
                      className={cn("mt-2.5 h-1 w-full rounded-full opacity-90 sm:mt-3", accent.bar)}
                      aria-hidden
                    />
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
